import {
    EncoderState,
    EncoderEvent,
    SuggestedChange,
} from './types'

export function createInitialState(
    originalBody: string,
    suggestedChange: SuggestedChange | null
): EncoderState {
    if (suggestedChange) {
        return {
            status: 'PENDING_APPROVAL',
            submissionId: suggestedChange.id,
            submissionBody: suggestedChange.body,
            submittedBy: suggestedChange.user_id,
            draftId: null,
            currentValue: suggestedChange.body,
            originalValue: originalBody,
            serverValidationErrors: [],
            hasUnsavedChanges: false,
        }
    }

    return {
        status: 'IDLE',
        submissionId: null,
        submissionBody: null,
        submittedBy: null,
        draftId: null,
        currentValue: originalBody,
        originalValue: originalBody,
        serverValidationErrors: [],
        hasUnsavedChanges: false,
    }
}

export function encoderReducer(
    state: EncoderState,
    event: EncoderEvent
): EncoderState {
    switch (event.type) {
        case 'TEXT_CHANGED': {
            const hasChanges = event.value !== state.originalValue
            const hasChangesFromSubmission =
                state.status === 'PENDING_APPROVAL' &&
                event.value !== state.submissionBody

            // If in PENDING_APPROVAL, stay there but track changes
            if (state.status === 'PENDING_APPROVAL') {
                return {
                    ...state,
                    currentValue: event.value,
                    hasUnsavedChanges: hasChangesFromSubmission,
                }
            }

            // If value equals original, go back to IDLE
            if (!hasChanges) {
                return {
                    ...state,
                    status: 'IDLE',
                    currentValue: event.value,
                    hasUnsavedChanges: false,
                    draftId: null,
                }
            }

            // Otherwise, we're EDITING
            return {
                ...state,
                status: 'EDITING',
                currentValue: event.value,
                hasUnsavedChanges: true,
            }
        }

        case 'VALIDATION_COMPLETED':
            return {
                ...state,
                serverValidationErrors: event.errors,
            }

        case 'DRAFT_SAVED':
            // Only transition to DRAFT_SAVED if we're EDITING
            if (state.status === 'EDITING') {
                return {
                    ...state,
                    status: 'DRAFT_SAVED',
                    draftId: event.draftId,
                    hasUnsavedChanges: false,
                }
            }
            return state

        case 'DRAFT_LOADED':
            // Load draft content - transition to DRAFT_SAVED
            return {
                ...state,
                status: 'DRAFT_SAVED',
                draftId: event.draft.id,
                currentValue: event.draft.body,
                hasUnsavedChanges: false,
            }

        case 'DRAFT_DELETED':
            if (state.status === 'DRAFT_SAVED') {
                return {
                    ...state,
                    status: 'EDITING',
                    draftId: null,
                }
            }
            return state

        case 'SUBMIT_STARTED':
            if (
                state.status === 'EDITING' ||
                state.status === 'DRAFT_SAVED'
            ) {
                return {
                    ...state,
                    status: 'SUBMITTING',
                }
            }
            return state

        case 'SUBMIT_COMPLETED':
            if (state.status === 'SUBMITTING') {
                return {
                    ...state,
                    status: 'PENDING_APPROVAL',
                    submissionId: event.submission.id,
                    submissionBody: state.currentValue,
                    submittedBy: event.submission.user_id,
                    draftId: null,
                    hasUnsavedChanges: false,
                }
            }
            return state

        case 'SUBMIT_FAILED':
            if (state.status === 'SUBMITTING') {
                return {
                    ...state,
                    status: state.draftId ? 'DRAFT_SAVED' : 'EDITING',
                }
            }
            return state

        case 'UPDATE_STARTED':
            if (state.status === 'PENDING_APPROVAL') {
                return {
                    ...state,
                    status: 'UPDATING',
                }
            }
            return state

        case 'UPDATE_COMPLETED':
            if (state.status === 'UPDATING') {
                return {
                    ...state,
                    status: 'PENDING_APPROVAL',
                    submissionBody: state.currentValue,
                    hasUnsavedChanges: false,
                }
            }
            return state

        case 'UPDATE_FAILED':
            if (state.status === 'UPDATING') {
                return {
                    ...state,
                    status: 'PENDING_APPROVAL',
                }
            }
            return state

        case 'CANCEL_STARTED':
            if (state.status === 'PENDING_APPROVAL') {
                return {
                    ...state,
                    status: 'CANCELLING',
                }
            }
            return state

        case 'CANCEL_COMPLETED':
            if (state.status === 'CANCELLING') {
                return {
                    ...state,
                    status: 'IDLE',
                    submissionId: null,
                    submissionBody: null,
                    submittedBy: null,
                    currentValue: state.originalValue,
                    hasUnsavedChanges: false,
                }
            }
            return state

        case 'CANCEL_FAILED':
            if (state.status === 'CANCELLING') {
                return {
                    ...state,
                    status: 'PENDING_APPROVAL',
                }
            }
            return state

        case 'APPROVE_STARTED':
            if (state.status === 'PENDING_APPROVAL') {
                return {
                    ...state,
                    status: 'APPROVING',
                }
            }
            return state

        case 'APPROVE_COMPLETED':
            if (state.status === 'APPROVING') {
                return {
                    ...state,
                    status: 'IDLE',
                    submissionId: null,
                    submissionBody: null,
                    submittedBy: null,
                    originalValue: state.currentValue,
                    hasUnsavedChanges: false,
                }
            }
            return state

        case 'APPROVE_FAILED':
            if (state.status === 'APPROVING') {
                return {
                    ...state,
                    status: 'PENDING_APPROVAL',
                }
            }
            return state

        case 'RESET':
            return {
                ...state,
                status: 'IDLE',
                currentValue: state.originalValue,
                submissionId: null,
                submissionBody: null,
                submittedBy: null,
                draftId: null,
                hasUnsavedChanges: false,
                serverValidationErrors: [],
            }

        case 'EXTERNAL_SUBMISSION_RECEIVED':
            // External submission from another user via realtime
            if (
                state.status === 'IDLE' ||
                state.status === 'EDITING' ||
                state.status === 'DRAFT_SAVED'
            ) {
                return {
                    ...state,
                    status: 'PENDING_APPROVAL',
                    submissionId: event.submission.id,
                    submissionBody: event.submission.body,
                    submittedBy: event.submission.user_id,
                    currentValue: event.submission.body,
                    hasUnsavedChanges: false,
                }
            }
            return state

        case 'EXTERNAL_SUBMISSION_DELETED':
            // External deletion from another user via realtime
            if (state.status === 'PENDING_APPROVAL') {
                return {
                    ...state,
                    status: 'IDLE',
                    submissionId: null,
                    submissionBody: null,
                    submittedBy: null,
                    currentValue: state.originalValue,
                    hasUnsavedChanges: false,
                }
            }
            return state

        case 'INITIAL_PENDING_LOADED':
            return {
                ...state,
                status: 'PENDING_APPROVAL',
                submissionId: event.submission.id,
                submissionBody: event.submission.body,
                submittedBy: event.submission.user_id,
                currentValue: event.submission.body,
                hasUnsavedChanges: false,
            }

        default:
            return state
    }
}

// Helper to check if user can perform actions
export function getSubmitterId(
    submittedBy: EncoderState['submittedBy']
): string | null {
    if (!submittedBy) return null
    if (typeof submittedBy === 'string') return submittedBy
    return submittedBy.id
}

export function getSubmitterName(
    submittedBy: EncoderState['submittedBy']
): string | null {
    if (!submittedBy) return null
    if (typeof submittedBy === 'string') return null
    return `${submittedBy.first_name} ${submittedBy.last_name}`
}
